import React from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { ANALYTICS_VIEW_MODE, AnalyticsViewMode } from '../types';

interface AnalyticsViewModeToggleProps {
  mode: AnalyticsViewMode;
  onChange: (mode: AnalyticsViewMode) => void;
}

/**
 * SHER-562. Picks which tier of the Outpatients tab is loaded. Quick view is the
 * default and fetches only the summary indicators; the detailed tier is fetched on
 * demand, so opening the page no longer fires every outpatient endpoint.
 */
const AnalyticsViewModeToggle: React.FC<AnalyticsViewModeToggleProps> = ({ mode, onChange }) => (
  <ToggleButtonGroup
    exclusive
    color="primary"
    sx={{
      // SHER-562 round 2. The control has to read as one of the filter row's controls, not
      // as a smaller thing parked beside them. Its neighbours — the two selects and "Apply
      // Changes" — are all one outlined-input line box tall: MUI's `1.4375em` plus 16.5px
      // of padding and a 1px border, top and bottom.
      //
      // Kept in `em` rather than written out as the pixel height it currently resolves to,
      // because `responsiveFontSizes` scales this row's font size per breakpoint: a pixel
      // constant would line up at one viewport width and be wrong at every other.
      //
      // `fontSize: inherit` is what makes the `em` resolve against the same font size the
      // inputs measure themselves from. Without it the group measures against the smaller
      // `typography.button` size and lands ~2px short. It does not change the labels: MUI
      // sets `typography.button` on the ToggleButton itself, not on the group.
      fontSize: 'inherit',
      height: 'calc(1.4375em + 35px)',
      // Without this the two labels wrap onto a second line, which is what made the group
      // taller than the row it sits in.
      '& .MuiToggleButton-root': { whiteSpace: 'nowrap' },
    }}
    value={mode}
    // MUI reports null when the selected button is clicked again. Passing that on would
    // leave the tab with no tier and nothing to fetch, so a click on the active mode is
    // simply a no-op.
    onChange={(_event, next: AnalyticsViewMode | null) => {
      if (next !== null) {
        onChange(next);
      }
    }}
    aria-label="analytics view mode"
  >
    <ToggleButton value={ANALYTICS_VIEW_MODE.QUICK}>
      <Translate id="hospital.analytics.view_mode.quick" />
    </ToggleButton>
    <ToggleButton value={ANALYTICS_VIEW_MODE.DETAILED}>
      <Translate id="hospital.analytics.view_mode.detailed" />
    </ToggleButton>
  </ToggleButtonGroup>
);

export default AnalyticsViewModeToggle;
